export interface RenderDensitySnapshot {
  readonly cssWidth: number;
  readonly devicePixelRatio: number;
  readonly resolution: number;
}

export function chooseRenderResolution(
  cssWidth: number,
  logicalWidth: number,
  devicePixelRatio: number,
  resolutionCap = 3,
): number {
  if (cssWidth <= 0 || logicalWidth <= 0) return 1;
  const layoutScale = cssWidth / logicalWidth;
  const physicalScale = Math.max(0.25, devicePixelRatio * layoutScale);
  const qualityFloor = cssWidth >= 700 ? 2 : cssWidth >= 480 ? 1.75 : 1.5;
  const desired = Math.max(qualityFloor, physicalScale);
  // Always round upwards: rounding down makes the same cabinet visibly softer
  // after a viewport or monitor switch even when its CSS size barely changed.
  const rounded = Math.ceil(desired * 4) / 4;
  return Math.min(resolutionCap, rounded);
}

export class DprObserver {
  private resizeObserver: ResizeObserver | null = null;
  private densityQuery: MediaQueryList | null = null;
  private animationFrame: number | null = null;
  private destroyed = false;

  constructor(
    private readonly host: HTMLElement,
    private readonly logicalWidth: number,
    private readonly onChange: (snapshot: RenderDensitySnapshot) => void,
    private readonly resolutionCap = 3,
  ) {}

  start(): void {
    if (this.destroyed || this.resizeObserver !== null) return;
    this.resizeObserver = new ResizeObserver(this.scheduleEmit);
    this.resizeObserver.observe(this.host);
    globalThis.addEventListener('resize', this.scheduleEmit, { passive: true });
    globalThis.visualViewport?.addEventListener('resize', this.scheduleEmit, { passive: true });
    this.bindDensityQuery();
    this.emit();
  }

  destroy(): void {
    this.destroyed = true;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    globalThis.removeEventListener('resize', this.scheduleEmit);
    globalThis.visualViewport?.removeEventListener('resize', this.scheduleEmit);
    this.densityQuery?.removeEventListener('change', this.handleDensityChange);
    this.densityQuery = null;
    if (this.animationFrame !== null) globalThis.cancelAnimationFrame(this.animationFrame);
    this.animationFrame = null;
  }

  private readonly handleDensityChange = (): void => {
    this.bindDensityQuery();
    this.scheduleEmit();
  };

  private readonly scheduleEmit = (): void => {
    if (this.destroyed || this.animationFrame !== null) return;
    this.animationFrame = globalThis.requestAnimationFrame(() => {
      this.animationFrame = null;
      this.emit();
    });
  };

  private bindDensityQuery(): void {
    this.densityQuery?.removeEventListener('change', this.handleDensityChange);
    const ratio = globalThis.devicePixelRatio || 1;
    this.densityQuery = globalThis.matchMedia?.(`(resolution: ${ratio}dppx)`) ?? null;
    this.densityQuery?.addEventListener('change', this.handleDensityChange);
  }

  private emit(): void {
    if (this.destroyed) return;
    const cssWidth = this.host.getBoundingClientRect().width;
    const devicePixelRatio = globalThis.devicePixelRatio || 1;
    this.onChange({
      cssWidth,
      devicePixelRatio,
      resolution: chooseRenderResolution(
        cssWidth,
        this.logicalWidth,
        devicePixelRatio,
        this.resolutionCap,
      ),
    });
  }
}
